import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

// The customer's Priority companies (ERP.priority_companies): an upload's priority_client
// - the email plus-tag or a WhatsApp number's company - must be one of them
interface PriorityCompaniesFieldProps {
  label: string;
  companies: string[];
  onChange: (companies: string[]) => void;
}

export const PriorityCompaniesField: React.FC<PriorityCompaniesFieldProps> = ({
  label,
  companies,
  onChange,
}) => {
  const { t } = useTranslation();
  const [newCompany, setNewCompany] = useState("");

  const handleAdd = () => {
    const name = newCompany.trim();
    if (name) {
      // Each company has its own document settings - a duplicate adds nothing
      if (!companies.includes(name)) {
        onChange([...companies, name]);
      }
      setNewCompany("");
    }
  };

  const handleRemove = (index: number) => {
    onChange(companies.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      {/* Input to add a new company */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "flex-start" }}>
        <TextField
          size="small"
          placeholder={t("ENTER_PRIORITY_COMPANY_PLACEHOLDER") || "Enter the Priority company code"}
          value={newCompany}
          onChange={(e) => setNewCompany(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ width: "300px" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={!newCompany.trim()}
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            minWidth: "100px",
          }}
        >
          {t("ADD_PRIORITY_COMPANY") || "Add Company"}
        </Button>
      </Stack>

      {/* List of added companies */}
      {companies.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          {companies.map((name, index) => (
            <Chip
              key={index}
              label={name}
              onDelete={() => handleRemove(index)}
              deleteIcon={<DeleteIcon />}
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
              }}
            />
          ))}
        </Box>
      )}

      {companies.length === 0 && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          {t("NO_PRIORITY_COMPANIES_ADDED") || "No companies added yet"}
        </Typography>
      )}
    </Box>
  );
};
